import { C } from "@/lib/brand";
import { daysUntil, vialStatusFor } from "@/lib/injectionApi";

// Standalone vial-status card. Shows reconstitution date, expiration
// countdown, and doses-remaining as a horizontal progress strip.
// Reused on the member dashboard surface and inside the Reference
// library's protocol tab.
export default function VialStatus({ vial, onMarkUsed, testId = "vial-status" }) {
  if (!vial || !vial.reconstitutedAt) {
    return (
      <div
        data-testid={`${testId}-empty`}
        style={{
          background: C.bone,
          padding: 18,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          color: C.warmGray,
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        No vial reconstituted yet. Mark your first reconstitution from your protocol card to start tracking expiration and doses remaining.
      </div>
    );
  }

  const status = vialStatusFor(vial);
  const days = daysUntil(vial.expiresAt);
  const total = vial.totalDoses || 0;
  const used = vial.dosesUsed || 0;
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.min(1, used / total) : 0;

  const tone =
    status === "expired"
      ? { bg: C.coral, fg: C.cream, accent: C.cream }
      : status === "expiring-soon"
      ? { bg: C.peach, fg: C.ink, accent: C.peachDark }
      : { bg: C.pure, fg: C.ink, accent: C.peachDeep };

  return (
    <article
      data-testid={testId}
      data-vial-status={status}
      style={{
        background: tone.bg,
        color: tone.fg,
        border: `1.5px solid ${C.ink}`,
        padding: 22,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: tone.accent }}>
        ★ YOUR CURRENT VIAL
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 18 }}>
        <Cell label="RECONSTITUTED" value={fmt(vial.reconstitutedAt)} fg={tone.fg} accent={tone.accent} />
        <Cell
          label={status === "expired" ? "EXPIRED" : "EXPIRES"}
          value={
            status === "expired"
              ? `${Math.abs(days)}d ago`
              : `${fmt(vial.expiresAt)} · ${days}d left`
          }
          fg={tone.fg}
          accent={tone.accent}
          large
        />
        {total > 0 && (
          <Cell
            label="DOSES"
            value={`${remaining} of ${total} left`}
            fg={tone.fg}
            accent={tone.accent}
          />
        )}
      </div>

      {total > 0 && (
        <div
          data-testid={`${testId}-progress`}
          style={{
            marginTop: 18,
            height: 6,
            background: status === "expired" ? "rgba(255,255,255,0.25)" : C.warmGray4,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              right: `${(1 - pct) * 100}%`,
              background: tone.accent,
              transition: "right 300ms",
            }}
          />
        </div>
      )}

      {status === "expiring-soon" && days >= 0 && (
        <p data-testid={`${testId}-warning`} style={{ marginTop: 14, fontSize: 13, lineHeight: 1.55, color: C.peachDark }}>
          <strong>Use the remaining doses then dispose.</strong> Your next vial should ship before this one expires — message your care team if you haven't heard.
        </p>
      )}
      {status === "expired" && (
        <p data-testid={`${testId}-expired`} style={{ marginTop: 14, fontSize: 13, lineHeight: 1.55 }}>
          <strong>Don't inject from this vial.</strong> Reconstituted vials are good for 30 days. Drop the needle + vial into the sharps container and start a new one.
        </p>
      )}

      {onMarkUsed && status !== "expired" && (
        <button
          type="button"
          data-testid={`${testId}-mark-used-btn`}
          onClick={onMarkUsed}
          style={{
            marginTop: 16,
            padding: "10px 18px",
            background: "transparent",
            border: `1.5px solid ${tone.fg}`,
            color: tone.fg,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            cursor: "pointer",
          }}
        >
          MARK A DOSE USED →
        </button>
      )}
    </article>
  );
}

function Cell({ label, value, fg, accent, large }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: accent }}>{label}</div>
      <div
        style={{
          marginTop: 4,
          fontSize: large ? 18 : 15,
          fontWeight: 800,
          color: fg,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
